import { useLocation, useHistory } from 'react-router'
import { useEffect, useRef } from 'react'
import classNames from 'classnames'
import { TransitionGroup, CSSTransition } from 'react-transition-group'

import useClickOutside from 'shared/hooks/useClickOutside'
import useKeyPress from 'shared/hooks/useKeyPress'
import { logout } from 'auth/actions'
import { userSelector, hasConfirmedWaitingListSelector } from 'auth/selectors'
import { authVisible, closeAuth } from '../actions'
import { ACTIVE_FORM_LOGIN, ACTIVE_FORM_SIGNUP } from '../constants'
import { headerStateSelector, isAuthPendingSelector, isAuthVisibleSelector } from '../selectors'

import CircleLoader from '@components/CircleLoader'

import IconButton from './IconButton'
import DropdownButton from './DropdownButton'
import DropdownLink from './DropdownLink'

import '../styles/animations.scss'

const $UserButton = ({ isActive, hasConfirmedWaitingList, onMouseEnter, translate }) => (
    <IconButton
        icon="user-login"
        size="xs"
        className={hasConfirmedWaitingList ? 'has-confirmed-waiting-list' : ''}
        isActive={true}
        alt={translate('header.user.icon.tooltip')}
        onMouseEnter={onMouseEnter}
    />
)

const UserButton = compose(
    connect(
        createStructuredSelector({
            hasConfirmedWaitingList: hasConfirmedWaitingListSelector,
        })
    ),
    withTranslate
)($UserButton)

const $LoginButton = ({ isActive, onClick, translate }) => (
    <IconButton
        icon="user"
        size={'xs'}
        isActive={true}
        alt={translate('header.login.icon.tooltip')}
        onClick={onClick}
    />
)

const LoginButton = withTranslate($LoginButton)

const links = hasConfirmedWaitingList => [
    { url: 'dashboard/transactions', label: 'header.user.manageBooking' },
    { url: 'dashboard/account', label: 'header.user.account' },
    { url: 'dashboard', label: 'header.user.dashboard', hasConfirmedWaitingList },
]

const $UserDropdown = ({ hasConfirmedWaitingList, onLogOutClick, closeUserDropdown }) => (
    <div className="arrow-box" onMouseLeave={closeUserDropdown}>
        <ul>
            {links(hasConfirmedWaitingList).map(({ url, label, hasConfirmedWaitingList }, index) => (
                <li key={index}>
                    <DropdownLink
                        url={url}
                        classNames={{
                            'nav-user-dropdown-button': true,
                            'has-confirmed-waiting-list': hasConfirmedWaitingList,
                        }}
                        onClick={closeUserDropdown}>
                        <T>{label}</T>
                    </DropdownLink>
                </li>
            ))}
            <li>
                <DropdownButton onClick={onLogOutClick}>
                    <T>header.user.logout</T>
                </DropdownButton>
            </li>
        </ul>
    </div>
)

const UserDropdown = connect(
    createStructuredSelector({
        hasConfirmedWaitingList: hasConfirmedWaitingListSelector,
    })
)($UserDropdown)

const UserContainer = ({ authVisible, header, isResponsive, user, authState, pending, logout, closeAuth }) => {
    const [isUserDropdownVisible, setIsUserDropdownVisible] = React.useState(false)
    const { hash } = useLocation()
    const history = useHistory()
    const ref = useRef()
    const transitionComponentRef = useRef()

    const setUserDropdownVisible = isOpen => {
        setIsUserDropdownVisible(prev => (isOpen === undefined ? !prev : isOpen))
    }

    const toggleUserDropdown = React.useCallback(() => setUserDropdownVisible(undefined), [])
    const closeUserDropdown = React.useCallback(() => {
        setUserDropdownVisible(false), []
    })

    const onLoginClick = () => {
        history.push('#login')
    }

    const onLogOutClick = () => {
        closeUserDropdown()
        logout()
        history.push({ hash: false })
    }

    useEffect(() => {
        if (!authState) {
            if (hash === '#login') {
                authVisible(ACTIVE_FORM_LOGIN)
            }
            if (hash === '#sign-up') {
                authVisible(ACTIVE_FORM_SIGNUP)
            }
        }
    }, [hash])

    useEffect(() => {
        if (user) {
            if (hash.length === 0) {
                closeAuth()
            }
        }
    }, [hash])

    useClickOutside(ref, closeUserDropdown)

    const escapePress = useKeyPress('Escape')
    useEffect(() => {
        if (escapePress) {
            closeUserDropdown()
        }
    }, [escapePress])

    return (
        <div
            ref={ref}
            className={classNames({
                'nav-user-container': true,
                'nav-user-container-responsive': isResponsive,
            })}>
            {pending === true ? (
                <div className="nav-icon-button">
                    <CircleLoader size="small" />
                </div>
            ) : user ? (
                <UserButton isActive={isUserDropdownVisible} onMouseEnter={toggleUserDropdown} />
            ) : (
                <LoginButton isActive={header.isAuthVisible} onClick={onLoginClick} />
            )}
            <TransitionGroup>
                {isUserDropdownVisible && (
                    <CSSTransition
                        nodeRef={transitionComponentRef}
                        key="user-dropdown"
                        timeout={{ enter: 100, exit: 100 }}
                        classNames="nav-dropdown-transition">
                        <div ref={transitionComponentRef}>
                            <UserDropdown onLogOutClick={onLogOutClick} closeUserDropdown={closeUserDropdown} />
                        </div>
                    </CSSTransition>
                )}
            </TransitionGroup>
        </div>
    )
}

const mapStateToProps = createStructuredSelector({
    header: headerStateSelector,
    user: userSelector,
    pending: isAuthPendingSelector,
    authState: isAuthVisibleSelector,
})

const mapDispatchToProps = {
    authVisible,
    closeAuth,
    logout,
}

export default connect(mapStateToProps, mapDispatchToProps)(UserContainer)
